import { useEffect, useMemo, useRef, useState } from 'react';
import { act } from '../lib/actions';
import { api, upload } from '../lib/api';
import { useNova } from '../lib/store';
import type { EmailAddress, EmailMessage, EmailView, FileView } from '../lib/types';
import { CannedPicker } from './Canned';
import { FileList } from './Files';
import { ChatWrap } from './ChatPane';
import { CHANNELS } from './ContextPanel';
import { Button, Elapsed, Icon } from './ui';

/**
 * One email conversation in the middle of the Work screen: every email in
 * its thread (earlier ones too), a reply box, and Close; then wrap-up with
 * an outcome, like a call. Emails show as plain text, never as HTML.
 */
export function EmailPane({ email }: { email: EmailView }) {
  const id = email.interactionId;
  const messages = useNova((s) => s.emailMessages[id]);

  useEffect(() => {
    let live = true;
    api<EmailMessage[]>('GET', `/emails/${id}/messages`)
      .then((m) => live && useNova.getState().set({ emailMessages: { ...useNova.getState().emailMessages, [id]: m } }))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [id]);

  const ch = CHANNELS.email;
  return (
    <>
      <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-white" style={{ background: ch.color }}>
          <Icon name={ch.icon} size={17} />
        </span>
        <div className="min-w-0">
          <p className="truncate font-semibold">{email.subject}</p>
          <p className="truncate text-[12px] text-ink-3">
            {email.customerName ? `${email.customerName} · ` : ''}
            {email.customerEmail}
            {email.mailboxName ? ` · to ${email.mailboxName}` : ''}
            {email.queueName ? ` · ${email.queueName}` : ''}
          </p>
        </div>
        {email.status === 'connected' ? (
          <span className="ml-auto flex shrink-0 items-center gap-3">
            <span className="text-[12px] text-ink-3">
              Yours for <Elapsed since={email.answeredAt} />
            </span>
            <Button tone="quiet" className="px-3 py-1.5 text-sm" onClick={() => void act(() => api('POST', `/emails/${id}/close`).then(() => true))}>
              Close
            </Button>
          </span>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-auto bg-abyss/40 p-4" aria-label="Conversation">
        {!messages ? <p className="text-sm text-ink-3">Loading the conversation…</p> : messages.map((m, n) => <Mail key={m.id} m={m} earlier={m.interactionId !== id} last={n === messages.length - 1} />)}
      </div>

      {email.status === 'connected' ? <Reply email={email} /> : null}
      {email.status === 'wrap_up' ? <ChatWrap id={id} name={email.customerName ?? email.customerEmail} what="email" /> : null}
    </>
  );
}

const who = (a: EmailAddress) => (a.name ? `${a.name} <${a.address}>` : a.address);
const size = (b: number) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** Splits off the quoted earlier messages under a reply ("On … wrote:" and "> " lines). */
function splitQuote(body: string): [string, string] {
  const lines = body.split('\n');
  const at = lines.findIndex((l, i) => /^On .+wrote:\s*$/.test(l.trim()) || (/^>/.test(l) && lines.slice(i).every((x) => !x.trim() || /^>/.test(x))));
  if (at <= 0) return [body, ''];
  return [lines.slice(0, at).join('\n').trimEnd(), lines.slice(at).join('\n')];
}

export function Mail({ m, earlier, last }: { m: EmailMessage; earlier: boolean; last: boolean }) {
  const [open, setOpen] = useState(last);
  const [showQuote, setShowQuote] = useState(false);
  const [text, quote] = useMemo(() => splitQuote(m.body), [m.body]);
  const mine = m.sender === 'agent';
  if (m.sender === 'system') return <p className="my-2 text-center text-[12px] text-ink-3">{m.body}</p>;
  const e = m.email;
  return (
    <article className={`mb-3 rounded-lg border bg-hull ${mine ? 'border-ion/40' : 'border-line'} ${earlier ? 'opacity-80' : ''}`} aria-label={mine ? 'Reply' : 'Customer email'}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-start gap-3 px-4 py-2.5 text-left" aria-expanded={open}>
        <span className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-semibold ${mine ? 'bg-ion text-on-accent' : 'bg-hull-2 text-ink-2'}`}>
          {(mine ? m.agentName ?? 'A' : e?.from.name ?? e?.from.address ?? '?').slice(0, 1).toUpperCase()}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">
            {mine ? `${m.agentName ?? 'Agent'} (${e?.from.address ?? 'reply'})` : e ? who(e.from) : 'Customer'}
          </span>
          <span className="block truncate text-[12px] text-ink-3">
            {open && e ? `To ${e.to.map(who).join(', ')}${e.cc.length ? ` · Cc ${e.cc.map(who).join(', ')}` : ''}` : text.replace(/\s+/g, ' ').slice(0, 140)}
          </span>
        </span>
        <span className="shrink-0 text-[11px] text-ink-3">
          {earlier ? 'Earlier · ' : ''}
          {new Date(m.at).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
      </button>
      {open ? (
        <div className="border-t border-line px-4 py-3">
          <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{text}</div>
          {quote ? (
            <>
              <button type="button" className="mt-2 text-[12px] text-ink-3 underline" onClick={() => setShowQuote((v) => !v)}>
                {showQuote ? 'Hide quoted text' : 'Show quoted text'}
              </button>
              {showQuote ? <div className="mt-2 whitespace-pre-wrap break-words border-l-2 border-line pl-3 text-[13px] text-ink-3">{quote}</div> : null}
            </>
          ) : null}
          {m.files?.length ? <FileList files={m.files} /> : null}
          {/* Named but not kept: too big, or from before Nova kept attachments */}
          {e?.attachments.some((a) => !m.files?.some((f) => f.filename === a.filename)) ? (
            <p className="mt-2 text-[12px] text-ink-3">
              In the mailbox only:{' '}
              {e.attachments
                .filter((a) => !m.files?.some((f) => f.filename === a.filename))
                .map((a) => `${a.filename} (${size(a.size)})`)
                .join(', ')}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function Reply({ email }: { email: EmailView }) {
  const [body, setBody] = useState('');
  const [cc, setCc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<FileView[]>([]);
  const ccList = cc
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  async function send(close: boolean) {
    if (!body.trim() || busy) return;
    setBusy(true);
    const ok = await act(
      () => api('POST', `/emails/${email.interactionId}/reply`, { body, cc: ccList, close, attachmentIds: files.map((f) => f.id) }),
      close ? 'Reply sent; email closed' : 'Reply sent',
    );
    setBusy(false);
    if (ok) {
      setBody('');
      setCc('');
      setFiles([]);
    }
  }
  return (
    <div className="shrink-0 border-t border-line p-3" aria-label="Reply by email">
      <div className="mb-2 flex items-center gap-2 text-[12px] text-ink-3">
        <span>
          To <span className="text-ink-2">{email.customerEmail}</span>, from {email.mailboxAddress}
        </span>
        {!showCc ? (
          <button type="button" className="ml-auto underline" onClick={() => setShowCc(true)}>
            Add Cc
          </button>
        ) : null}
      </div>
      {showCc ? (
        <input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Cc: addresses, separated by commas" aria-label="Cc" className="mb-2 w-full rounded-lg border border-line bg-hull px-3 py-1.5 text-sm outline-none focus:border-ion" />
      ) : null}
      <textarea
        value={body}
        rows={5}
        maxLength={50000}
        aria-label="Reply"
        placeholder="Write your reply. The customer's email is quoted under it, with the mailbox's signature."
        onChange={(e) => setBody(e.target.value)}
        className="w-full resize-y rounded-lg border border-line bg-hull px-3 py-2 text-sm outline-none focus:border-ion"
      />
      <Attach files={files} onChange={setFiles} interactionId={email.interactionId} />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button tone="primary" className="px-4 py-2" disabled={busy || !body.trim()} onClick={() => void send(true)}>
          Send and close
        </Button>
        <Button tone="quiet" className="px-4 py-2" disabled={busy || !body.trim()} onClick={() => void send(false)}>
          Send
        </Button>
        <CannedPicker channel="email" customer={email.customerName} onPick={(t) => setBody((b) => (b.trim() ? `${b.trimEnd()}\n\n${t}` : t))} />
        {busy ? <span className="self-center text-[12px] text-ink-3">Sending…</span> : null}
      </div>
    </div>
  );
}

/** Files to send with an email: uploaded as they are picked, removable until it goes. */
export function Attach({ files, onChange, interactionId }: { files: FileView[]; onChange: (f: FileView[]) => void; interactionId?: string }) {
  const picker = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  async function add(list: FileList) {
    setBusy(true);
    const added: FileView[] = [];
    for (const f of Array.from(list)) {
      const r = await act(() => upload<FileView>(`/emails/files${interactionId ? `?interactionId=${interactionId}` : ''}`, f));
      if (r) added.push(r);
    }
    setBusy(false);
    onChange([...files, ...added]);
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" aria-label="Attachments">
      <button type="button" onClick={() => picker.current?.click()} disabled={busy} className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-[12px] text-ink-2 hover:border-line-strong">
        <Icon name="clip" size={13} /> {busy ? 'Attaching…' : 'Attach files'}
      </button>
      <input
        ref={picker}
        type="file"
        multiple
        hidden
        aria-label="Files to attach"
        onChange={(e) => {
          if (e.target.files?.length) void add(e.target.files);
          e.target.value = '';
        }}
      />
      {files.map((f) => (
        <span key={f.id} className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-[12px]">
          {f.filename} <span className="text-ink-3">{size(f.size)}</span>
          <button
            type="button"
            aria-label={`Remove ${f.filename}`}
            className="ml-0.5 text-ink-3 hover:text-break"
            onClick={() => {
              onChange(files.filter((x) => x.id !== f.id));
              void api('DELETE', `/emails/files/${f.id}`).catch(() => undefined);
            }}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}
